import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IMPORT_SOURCES,
  MERGE_STRATEGIES,
  type ImportSource,
  type MergeStrategy,
  type ImportDiff,
} from '@system-atlas/shared';
import { Button, Label, Select } from '../../components/ui';
import { useToast } from '../../components/toast';
import { IMPORT_SCRIPTS } from './scripts';

interface ImportDialogProps {
  onPreview: (source: ImportSource, text: string, strategy: MergeStrategy) => { diff?: ImportDiff; error?: string };
  onApply: (source: ImportSource, text: string, strategy: MergeStrategy) => { error?: string };
  onClose: () => void;
}

/** Import-Dialog (Baustein G): Quelle waehlen, Skript kopieren, CSV laden, Strategie + Vorschau, uebernehmen. */
export function ImportDialog({ onPreview, onApply, onClose }: ImportDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [source, setSource] = useState<ImportSource>('azure-users');
  const [strategy, setStrategy] = useState<MergeStrategy>('merge');
  const [fileName, setFileName] = useState('');
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ diff?: ImportDiff; error?: string } | null>(null);

  useEffect(() => {
    if (!text) {
      setResult(null);
      return;
    }
    setResult(onPreview(source, text, strategy));
  }, [text, source, strategy, onPreview]);

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ''));
    reader.onerror = () => toast.error(t('editor.import.readError'));
    reader.readAsText(file, 'utf-8');
  };

  const copyScript = () => {
    void navigator.clipboard
      .writeText(IMPORT_SCRIPTS[source])
      .then(() => toast.success(t('editor.import.copied')))
      .catch(() => toast.error(t('editor.import.copyFailed')));
  };

  const apply = () => {
    const res = onApply(source, text, strategy);
    if (res.error) {
      setResult({ error: res.error });
      return;
    }
    toast.success(t('editor.import.done'));
    onClose();
  };

  const diff = result?.diff;
  const canApply = !!diff && !result?.error;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('editor.import.title')}
      onMouseDown={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('editor.import.title')}</h2>
          <Button variant="ghost" className="px-2" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </Button>
        </div>

        <div className="mb-3">
          <Label htmlFor="imp-source">{t('editor.import.source')}</Label>
          <Select id="imp-source" className="w-full" value={source} onChange={(e) => setSource(e.target.value as ImportSource)}>
            {IMPORT_SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`editor.import.sourceOption.${s}`)}
              </option>
            ))}
          </Select>
        </div>

        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between">
            <Label>{t('editor.import.script')}</Label>
            <Button variant="secondary" className="px-2 py-1 text-xs" onClick={copyScript}>
              {t('editor.import.copyScript')}
            </Button>
          </div>
          <pre className="max-h-40 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-800">
            {IMPORT_SCRIPTS[source]}
          </pre>
        </div>

        <div className="mb-3">
          <Label htmlFor="imp-file">{t('editor.import.file')}</Label>
          <input id="imp-file" type="file" accept=".csv,.txt" onChange={onFile} className="block w-full text-sm" />
          {fileName && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{fileName}</p>}
        </div>

        <div className="mb-3">
          <Label>{t('editor.import.strategy')}</Label>
          <div className="space-y-1">
            {MERGE_STRATEGIES.map((s) => (
              <label key={s} className="flex items-start gap-2 text-sm">
                <input type="radio" name="imp-strategy" checked={strategy === s} onChange={() => setStrategy(s)} className="mt-1" />
                <span>
                  <span className="font-medium">{t(`editor.import.strategyOption.${s}.label`)}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{t(`editor.import.strategyOption.${s}.hint`)}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {result?.error && (
          <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {result.error}
          </p>
        )}
        {diff && (
          <div className="mb-3 rounded-md border border-slate-200 p-3 text-sm dark:border-slate-700">
            <p>
              {t('editor.import.previewSummary', {
                recognized: diff.recognized,
                added: diff.added,
                updated: diff.updated,
                removed: diff.removed,
              })}
            </p>
            {strategy === 'mirror' && diff.removed > 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                {t('editor.import.mirrorWarning', { removed: diff.removed })}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
          <Button variant="secondary" onClick={onClose}>
            {t('editor.import.cancel')}
          </Button>
          <Button onClick={apply} disabled={!canApply}>
            {t('editor.import.apply')}
          </Button>
        </div>
      </div>
    </div>
  );
}
